import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, BadgePercent, Banknote, BookOpenText, Landmark, Receipt } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { Link, useParams } from 'react-router'
import { ApiError, errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { StatCard } from '@/components/ui/StatCard'
import { useAuth } from '@/features/auth/useAuth'
import { discountsQuery, ledgerQuery, useAssignStudentDiscount } from '@/features/finance/api'
import { localizePeriodLabel, methodLabel } from '@/features/finance/status'
import type { Ledger, LedgerEntry, PaymentMethod } from '@/features/finance/types'
import { useMoney } from '@/features/finance/useCurrency'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatDate, formatNumber } from '@/utils/format'
import { sectionLabel } from '@/features/academic/sectionLabel'

const typeTone: Record<LedgerEntry['type'], 'info' | 'success' | 'danger' | 'neutral'> = {
  invoice: 'info',
  payment: 'success',
  reversal: 'danger',
  cancellation: 'neutral',
}

const ENTRY_TYPE_LABEL: Record<LedgerEntry['type'], string> = {
  invoice: 'invoice',
  payment: 'payment',
  reversal: 'reversal',
  cancellation: 'cancellation',
}

type TFunction = ReturnType<typeof useTranslation>['t']

/** The API describes ledger entries in English; rebuild them in the active language. */
function entryDescription(e: LedgerEntry, t: TFunction): string {
  const d = e.description
  let m: RegExpExecArray | null
  if ((m = /^Invoice · (.+)$/.exec(d)))
    return t('Invoice · {{period}}', { period: localizePeriodLabel(m[1] ?? '') })
  if ((m = /^Invoice cancelled(: .*)?$/.exec(d))) return t('Invoice cancelled') + (m[1] ?? '')
  if ((m = /^Payment reversed(: .*)?$/.exec(d))) return t('Payment reversed') + (m[1] ?? '')
  if ((m = /^Payment for (\S+) \((.+)\)$/.exec(d))) {
    const key = (m[2] ?? '').replace(/ /g, '_') as PaymentMethod
    const method = key in methodLabel ? t(methodLabel[key]).toLowerCase() : (m[2] ?? '')
    return t('Payment for {{number}} ({{method}})', { number: m[1], method })
  }
  return d
}

function DiscountCard({ ledger }: { ledger: Ledger }) {
  const { t } = useTranslation('finance')
  const { can } = useAuth()
  const manage = can('finance.manage')
  const discounts = useQuery({ ...discountsQuery(), enabled: manage })
  const assign = useAssignStudentDiscount()
  const [value, setValue] = useState(ledger.discount ? String(ledger.discount.id) : '')
  const changed = value !== (ledger.discount ? String(ledger.discount.id) : '')

  const save = () =>
    assign.mutate(
      { studentId: ledger.student.id, discountId: value ? Number(value) : null },
      {
        onSuccess: (r) =>
          toast.success(
            r.discount ? t('Discount assigned') : t('Discount removed'),
            r.discount?.name ?? ledger.student.name,
          ),
        onError: (e) => toast.error(t('Could not update the discount'), errorMessage(e)),
      },
    )

  return (
    <Card>
      <CardHeader
        title={t('Standing discount')}
        description={t('Applied to invoices generated from now on.')}
        icon={<BadgePercent />}
      />
      <CardBody className="space-y-3">
        {manage ? (
          <>
            <label className="sr-only" htmlFor="student-discount">
              {t('Discount')}
            </label>
            <Select
              id="student-discount"
              placeholder={t('No discount')}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              options={(discounts.data ?? [])
                .filter((d) => d.isActive || String(d.id) === value)
                .map((d) => ({ value: d.id, label: d.name }))}
            />
            <Button size="sm" onClick={save} loading={assign.isPending} disabled={!changed}>
              {t('Save discount')}
            </Button>
          </>
        ) : (
          <p className="text-sm text-ink">{ledger.discount?.name ?? t('None')}</p>
        )}
      </CardBody>
    </Card>
  )
}

export function StudentLedgerPage() {
  const { t } = useTranslation('finance')
  const withGuardians = useGuardians()
  const id = Number(useParams().id)
  const { data, isPending, error, refetch } = useQuery(ledgerQuery(id))
  const { money } = useMoney()
  useDocumentTitle(data ? t('{{name}} · Ledger', { name: data.student.name }) : 'Student ledger')

  if (isPending) return <LoadingState />
  if (error)
    return error instanceof ApiError && error.status === 404 ? (
      <NotFoundState resource="student" homeHref="/admin/finance/invoices" />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )

  const s = data.summary

  return (
    <>
      <PageHeader
        title={data.student.name}
        description={`${data.student.studentCode}${data.student.className ? ` · ${sectionLabel(data.student.className, data.student.sectionName, ' – ')}` : ''}`}
        breadcrumbs={[
          { label: t('Finance') },
          { label: t('Invoices'), to: '/admin/finance/invoices' },
          { label: t('Student ledger') },
        ]}
        homeHref="/admin"
        actions={
          <Button asChild variant="secondary" leftIcon={<Receipt />}>
            <Link to={`/admin/finance/invoices?search=${encodeURIComponent(data.student.studentCode)}`}>
              {t('Invoices')}
            </Link>
          </Button>
        }
      />
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('Invoiced')}
          value={money(s.totalInvoiced)}
          icon={<Receipt />}
          tone="info"
          hint={t('{{count}} invoices', { count: s.invoiceCount, formatted: formatNumber(s.invoiceCount) })}
        />
        <StatCard label={t('Paid')} value={money(s.totalPaid)} icon={<Banknote />} tone="success" />
        <StatCard
          label={t('Outstanding')}
          value={money(s.outstanding)}
          icon={<Landmark />}
          tone="brand"
          hint={t('{{count}} open invoices', {
            count: s.openInvoiceCount,
            formatted: formatNumber(s.openInvoiceCount),
          })}
        />
        <StatCard label={t('Overdue')} value={money(s.overdue)} icon={<AlertTriangle />} tone="danger" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <Card>
          <CardHeader
            title={t('Account history')}
            description={
              withGuardians
                ? t('Running balance: positive means the family owes the school.')
                : t('Running balance: positive means the student owes the school.')
            }
            icon={<BookOpenText />}
          />
          {data.entries.length === 0 ? (
            <EmptyState
              title={t('No invoices yet')}
              description={t('This student has not been billed.')}
              compact
            />
          ) : (
            <div className="scrollbar-thin overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('Ledger entries')}</caption>
                <thead>
                  <tr className="border-b border-line bg-surface-muted text-2xs tracking-wider text-muted uppercase">
                    <th scope="col" className="px-5 py-2.5 text-left font-semibold">
                      {t('Date')}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left font-semibold">
                      {t('Entry')}
                    </th>
                    <th scope="col" className="hidden px-4 py-2.5 text-right font-semibold sm:table-cell">
                      {t('Charge')}
                    </th>
                    <th scope="col" className="hidden px-4 py-2.5 text-right font-semibold sm:table-cell">
                      {t('Credit')}
                    </th>
                    <th scope="col" className="px-5 py-2.5 text-right font-semibold">
                      {t('Balance')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e, i) => (
                    <tr
                      key={`${e.type}-${e.reference}-${i}`}
                      className="border-b border-line last:border-b-0"
                    >
                      <td className="px-5 py-3 whitespace-nowrap text-ink-soft">{formatDate(e.date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge size="sm" tone={typeTone[e.type]}>
                            {t(ENTRY_TYPE_LABEL[e.type])}
                          </Badge>
                          {e.invoiceId ? (
                            <Link
                              to={`/admin/finance/invoices/${e.invoiceId}`}
                              className="font-mono text-xs text-ink hover:text-brand-700"
                            >
                              {e.reference}
                            </Link>
                          ) : (
                            <span className="font-mono text-xs">{e.reference}</span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-muted">{entryDescription(e, t)}</p>
                        <p className="mt-0.5 text-xs text-ink-soft tabular sm:hidden">
                          {e.debit !== '0.00' ? `+${money(e.debit)}` : `−${money(e.credit)}`}
                        </p>
                      </td>
                      <td className="hidden px-4 py-3 text-right tabular sm:table-cell">
                        {e.debit !== '0.00' ? money(e.debit) : ''}
                      </td>
                      <td className="hidden px-4 py-3 text-right tabular sm:table-cell">
                        {e.credit !== '0.00' ? money(e.credit) : ''}
                      </td>
                      <td
                        className={cn(
                          'px-5 py-3 text-right font-medium tabular',
                          Number(e.balance) > 0 ? 'text-ink' : 'text-muted',
                        )}
                      >
                        {money(e.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <div className="space-y-6">
          <DiscountCard key={data.discount?.id ?? 'none'} ledger={data} />
          <Card>
            <CardBody className="text-sm">
              <Link
                to={`/admin/students/${data.student.id}`}
                className="font-medium text-brand-700 hover:underline"
              >
                {t('Open student record')}
              </Link>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  )
}
